import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { Auth0Provider, AppState } from '@auth0/auth0-react';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { TooltipProvider } from './components/Tooltip';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

// Unregister any stale service workers and purge their caches. We do not ship
// a service worker; if one is alive it is from a prior deploy or a stray
// registration and will serve stale chunks across deploys.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((regs) => {
    regs.forEach((r) => { r.unregister().catch(() => {}); });
  }).catch(() => {});
  if (typeof caches !== 'undefined') {
    caches.keys().then((keys) => {
      keys.forEach((k) => { caches.delete(k).catch(() => {}); });
    }).catch(() => {});
  }
}

// Log build version so we can diagnose stale-bundle issues per user
fetch('/version.json?t=' + Date.now(), { cache: 'no-store' })
  .then(r => r.ok ? r.json() : null)
  .then(data => {
    if (data?.version) {
      console.info('[Atiende build]', data.version, '|', data.builtAt ?? '');
    }
  })
  .catch(() => {});

// Suppress benign ResizeObserver loop warnings from dev overlay
const _resizeObserverErr = window.onerror;
window.onerror = (msg, ...args) => {
  if (typeof msg === 'string' && msg.includes('ResizeObserver loop')) return true;
  return _resizeObserverErr ? _resizeObserverErr(msg, ...args) as boolean : false;
};
window.addEventListener('error', (e) => {
  if (e.message.includes('ResizeObserver loop')) e.stopImmediatePropagation();
}, true);
const _OriginalResizeObserver = window.ResizeObserver;
window.ResizeObserver = class extends _OriginalResizeObserver {
  constructor(callback: ResizeObserverCallback) {
    super((entries, observer) => {
      requestAnimationFrame(() => callback(entries, observer));
    });
  }
};

// Auth0Provider must be inside BrowserRouter so onRedirectCallback can use
// useNavigate to clean up ?code=&state= and trigger a proper React Router
// navigation — without this the callback URL params linger and isLoading hangs.
function Auth0ProviderWithNavigate({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();

  const onRedirectCallback = React.useCallback((appState?: AppState) => {
    navigate(appState?.returnTo ?? window.location.pathname, { replace: true });
  }, [navigate]);

  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN!}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID!}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email offline_access',
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const isE2E = process.env.REACT_APP_E2E === 'true';

// Loaded only when REACT_APP_E2E=true — dynamic import so the chunk is on
// the dead branch and Terser can eliminate it from the production build.
const E2ERoot = React.lazy(() =>
  import('./test-utils/MockAuth0Provider').then(({ MockAuth0Provider }) => ({
    default: function E2EApp() {
      return (
        <MockAuth0Provider>
          <AuthProvider>
            <TooltipProvider delayDuration={0}>
              <App />
            </TooltipProvider>
          </AuthProvider>
        </MockAuth0Provider>
      );
    },
  }))
);

root.render(
  <ThemeProvider>
    <BrowserRouter>
      {isE2E ? (
        <React.Suspense fallback={null}>
          <E2ERoot />
        </React.Suspense>
      ) : (
        <Auth0ProviderWithNavigate>
          <AuthProvider>
            <TooltipProvider delayDuration={0}>
              <App />
            </TooltipProvider>
          </AuthProvider>
        </Auth0ProviderWithNavigate>
      )}
    </BrowserRouter>
  </ThemeProvider>
);

reportWebVitals();
